import React, { useEffect, useState } from 'react';
import {
  getSeasonPassDetail,
  updateSeasonPass,
  getOrderProducts,
  uploadSeasonPassPhoto,
  deleteSeasonPassPhoto,
} from '../api';
import AppHeader from './AppHeader';
import { US_STATES } from '../utils/usStates';
import { isEmployee } from '../utils/roles';
import { parseEntitlements } from '../utils/entitlements';
import PerksEditor from './PerksEditor';

function SeasonPassDetail({ user, onLogout, onNavigate, seasonPassId, returnTo = 'search' }) {
  const [pass, setPass] = useState(null);
  const [cases, setCases] = useState([]);
  const [compensations, setCompensations] = useState([]);
  const [form, setForm] = useState(null);
  const [passTypes, setPassTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const employeeView = isEmployee(user);

  const loadDetail = async (id) => {
    setLoading(true);
    setError('');
    try {
      const data = await getSeasonPassDetail(id);
      setPass(data.seasonPass);
      setForm({
        ...data.seasonPass,
        entitlements: parseEntitlements(data.seasonPass.entitlements || []),
      });
      setCases(data.cases || []);
      setCompensations(data.compensations || []);
    } catch (err) {
      setError(err.error || 'Unable to load season pass');
      setPass(null);
      setForm(null);
      setCases([]);
      setCompensations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getOrderProducts()
      .then((data) => setPassTypes(data.seasonPassTypes || []))
      .catch(() => setPassTypes([]));
  }, []);

  useEffect(() => {
    if (seasonPassId) {
      loadDetail(seasonPassId);
    }
  }, [seasonPassId]);

  const applyPassType = (productCode) => {
    const type = passTypes.find((entry) => entry.code === productCode);
    setForm((prev) => ({
      ...prev,
      productCode,
      entitlements: parseEntitlements(type?.entitlements || []),
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'productCode') {
      applyPassType(value);
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };


  const handleSave = async (e) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const data = await updateSeasonPass(form.id, {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        dateOfBirth: form.dateOfBirth,
        phone: form.phone,
        address: form.address,
        address2: form.address2,
        city: form.city,
        state: form.state,
        zip: form.zip,
        orderId: form.orderId,
        productCode: form.productCode,
        seasonPassId: form.seasonPassId,
        entitlements: form.entitlements || [],
      });
      setMessage(`Season pass ${data.seasonPass.seasonPassId} saved`);
      await loadDetail(data.seasonPass.id);
    } catch (err) {
      setError(err.error || 'Unable to update season pass');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !pass?.id) return;
    setUploadingPhoto(true);
    setError('');
    setMessage('');
    try {
      const data = await uploadSeasonPassPhoto(pass.id, file);
      setMessage('Passholder photo saved (resized WebP)');
      setPass(data.seasonPass);
      setForm((prev) => ({ ...prev, ...data.seasonPass }));
    } catch (err) {
      setError(err.error || 'Unable to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!pass?.id) return;
    setUploadingPhoto(true);
    setError('');
    setMessage('');
    try {
      const data = await deleteSeasonPassPhoto(pass.id);
      setMessage('Passholder photo removed');
      setPass(data.seasonPass);
      setForm((prev) => ({ ...prev, ...data.seasonPass, photoUrl: '' }));
    } catch (err) {
      setError(err.error || 'Unable to remove photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const openNewCase = () => {
    if (!pass) return;
    onNavigate('cases', {
      returnToSeasonPassId: pass.id,
      casePrefill: {
        guestName: `${pass.firstName || ''} ${pass.lastName || ''}`.trim(),
        guestEmail: pass.email || '',
        guestPhone: pass.phone || '',
        ticketType: 'Enchanted Parks',
        ticketId: pass.seasonPassId || '',
        seasonPassId: pass.seasonPassId || '',
        subject: '',
        description: '',
        origin: 'In Person',
        priority: 'Medium',
        status: 'New',
      },
    });
  };

  const openCompensation = () => {
    if (!pass) return;
    onNavigate('submit', {
      prefill: {
        guestName: `${pass.firstName || ''} ${pass.lastName || ''}`.trim(),
        guestEmail: pass.email || '',
        ticketType: 'Enchanted Parks',
        ticketId: pass.seasonPassId || '',
      },
    });
  };

  const formatWhen = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  };

  const statusClass = (status) =>
    `case-status status-${String(status || '')
      .toLowerCase()
      .replace(/\s+/g, '-')}`;

  return (
    <div className="container wide-container season-pass-detail-page">
      <AppHeader
        user={user}
        pageTitle="Season Pass"
        subtitle="Guest profile, cases, and compensation history"
        onNavigate={onNavigate}
        onLogout={onLogout}
        active="season-pass-detail"
      />

      <div className="detail-toolbar">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => onNavigate(returnTo || 'search')}
        >
          ← Back to search
        </button>
        {pass && !employeeView && (
          <>
            <button type="button" className="btn-primary" onClick={openNewCase}>
              + New Case
            </button>
            <button type="button" className="btn-secondary" onClick={openCompensation}>
              + Record Compensation
            </button>
            {pass.orderId && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => onNavigate('orders', { orderId: pass.orderId })}
              >
                View Order
              </button>
            )}
          </>
        )}
        {pass && employeeView && pass.orderId && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => onNavigate('orders', { orderId: pass.orderId })}
          >
            View Order
          </button>
        )}
      </div>

      {error && <div className="error">{error}</div>}
      {message && <div className="success">{message}</div>}
      {loading && <p className="subtitle">Loading season pass…</p>}

      {!loading && form && (
        <>
          <section className="case-panel pass-profile-panel">
            <div className="section-header">
              <div>
                <h2 style={{ marginBottom: 4 }}>{form.seasonPassId}</h2>
                <p className="subtitle" style={{ marginBottom: 0 }}>
                  {form.productName || form.productCode} · {form.firstName} {form.lastName}
                </p>
              </div>
            </div>

            <div className="pass-photo-block">
              {form.photoUrl ? (
                <img
                  className="pass-photo"
                  src={form.photoUrl}
                  alt={`${form.firstName || ''} ${form.lastName || ''}`.trim() || 'Passholder'}
                />
              ) : (
                <div className="pass-photo placeholder">No photo</div>
              )}
              <div className="pass-photo-actions">
                <label className="btn-secondary pass-photo-upload">
                  {uploadingPhoto ? 'Uploading…' : 'Upload photo'}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    hidden
                    disabled={uploadingPhoto}
                    onChange={handlePhotoSelected}
                  />
                </label>
                {form.photoUrl && (
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={uploadingPhoto}
                    onClick={handleRemovePhoto}
                  >
                    Remove photo
                  </button>
                )}
                <p className="subtitle" style={{ margin: 0 }}>
                  Stored on this server as compressed WebP (max 800px).
                </p>
              </div>
            </div>

            <form className="case-form" onSubmit={handleSave}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="pass-productCode">Pass Type</label>
                  <select
                    id="pass-productCode"
                    name="productCode"
                    value={form.productCode || ''}
                    onChange={handleChange}
                  >
                    {passTypes.map((type) => (
                      <option key={type.code} value={type.code}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="pass-seasonPassId">Season Pass ID *</label>
                  <input
                    id="pass-seasonPassId"
                    name="seasonPassId"
                    value={form.seasonPassId || ''}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="pass-orderId">Order ID</label>
                  <input
                    id="pass-orderId"
                    name="orderId"
                    value={form.orderId || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="pass-firstName">First Name *</label>
                  <input
                    id="pass-firstName"
                    name="firstName"
                    value={form.firstName || ''}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="pass-lastName">Last Name *</label>
                  <input
                    id="pass-lastName"
                    name="lastName"
                    value={form.lastName || ''}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="pass-dateOfBirth">Date of Birth</label>
                  <input
                    id="pass-dateOfBirth"
                    name="dateOfBirth"
                    type="date"
                    value={form.dateOfBirth || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="pass-email">Email</label>
                  <input
                    id="pass-email"
                    name="email"
                    type="email"
                    value={form.email || ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="pass-phone">Phone</label>
                  <input
                    id="pass-phone"
                    name="phone"
                    value={form.phone || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="pass-address">Address</label>
                <input
                  id="pass-address"
                  name="address"
                  value={form.address || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="pass-address2">Address 2</label>
                <input
                  id="pass-address2"
                  name="address2"
                  value={form.address2 || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="order-address-grid">
                <div className="form-group">
                  <label htmlFor="pass-city">City</label>
                  <input
                    id="pass-city"
                    name="city"
                    value={form.city || ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="pass-state">State</label>
                  <select
                    id="pass-state"
                    name="state"
                    value={form.state || ''}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    {US_STATES.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="pass-zip">Zip</label>
                  <input
                    id="pass-zip"
                    name="zip"
                    value={form.zip || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <PerksEditor
                idPrefix="pass-perk"
                perks={form.entitlements || []}
                onChange={(entitlements) => setForm((prev) => ({ ...prev, entitlements }))}
              />

              <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save Pass'}
                </button>
              </div>
            </form>
          </section>

          <div className="pass-related-stack">
            <section className="case-panel">
              <div className="section-header">
                <h3>Cases</h3>
                <button type="button" className="btn-table" onClick={openNewCase}>
                  New Case
                </button>
              </div>
              <p className="subtitle">
                Guest questions and service cases linked by season pass ID or email.
              </p>
              {cases.length === 0 ? (
                <p className="subtitle">No cases for this guest yet.</p>
              ) : (
                <div className="table-scroll">
                  <table className="pass-related-table">
                    <thead>
                      <tr>
                        <th>Case</th>
                        <th>Subject</th>
                        <th>Status</th>
                        <th>Updated</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cases.map((entry) => (
                        <tr key={entry.id}>
                          <td>{entry.caseNumber}</td>
                          <td>{entry.subject}</td>
                          <td>
                            <span className={statusClass(entry.status)}>{entry.status}</span>
                          </td>
                          <td>{formatWhen(entry.updatedAt)}</td>
                          <td>
                            <button
                              type="button"
                              className="btn-table"
                              onClick={() =>
                                onNavigate('cases', {
                                  caseId: entry.id,
                                  returnToSeasonPassId: pass.id,
                                })
                              }
                            >
                              Open
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="case-panel">
              <div className="section-header">
                <h3>Compensation</h3>
                <button type="button" className="btn-table" onClick={openCompensation}>
                  Record
                </button>
              </div>
              <p className="subtitle">
                Past resolutions and compensation issued to this guest.
              </p>
              {compensations.length === 0 ? (
                <p className="subtitle">No compensation records for this guest yet.</p>
              ) : (
                <div className="table-scroll">
                  <table className="pass-related-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Concern</th>
                        <th>Resolution</th>
                        <th>Case</th>
                      </tr>
                    </thead>
                    <tbody>
                      {compensations.map((entry) => (
                        <tr key={entry.id}>
                          <td className="nowrap">{formatWhen(entry.dateSubmitted)}</td>
                          <td>{entry.guestConcern || '—'}</td>
                          <td>
                            {entry.resolution}
                            {entry.resolutionOther ? ` — ${entry.resolutionOther}` : ''}
                          </td>
                          <td className="nowrap">
                            {entry.caseId ? (
                              <button
                                type="button"
                                className="link-button"
                                onClick={() => onNavigate('cases', { caseId: entry.caseId })}
                              >
                                {entry.caseNumber || 'Open'}
                              </button>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}

export default SeasonPassDetail;
